import * as React from 'react'

const MIN_EXPANDED_PERCENT = 12
const DEFAULT_RATIO = 50
const SPLIT_HIT_ZONE_PX = 12

type CollapsedSide = 'md' | 'html' | null

function isSplitView(mdSection: HTMLElement, htmlSection: HTMLElement) {
  return !mdSection.classList.contains('in-visible') && !htmlSection.classList.contains('in-visible')
}

function getSplitX(mdSection: HTMLElement) {
  return mdSection.getBoundingClientRect().right
}

function isNearSplit(clientX: number, mdSection: HTMLElement) {
  return Math.abs(clientX - getSplitX(mdSection)) <= SPLIT_HIT_ZONE_PX
}

/**
 * Adds a draggable ↔ split between markdown editor and preview panes
 * in react-markdown-editor-lite split view.
 */
export function useMarkdownEditorSplitResize(containerRef: React.RefObject<HTMLElement | null>) {
  React.useLayoutEffect(() => {
    const container = containerRef.current
    if (!container) return

    let disposed = false
    let teardown: (() => void) | undefined
    let attachedContainer: HTMLElement | null = null

    const attach = () => {
      if (disposed) return

      const editorContainer = container.querySelector('.editor-container') as HTMLElement | null
      const mdSection = editorContainer?.querySelector('.sec-md') as HTMLElement | null
      const htmlSection = editorContainer?.querySelector('.sec-html') as HTMLElement | null

      if (!editorContainer || !mdSection || !htmlSection) return
      if (editorContainer === attachedContainer) return

      teardown?.()
      attachedContainer = editorContainer

      let isDragging = false
      let collapsedSide: CollapsedSide = null
      let lastRatio = DEFAULT_RATIO

      const applyRatio = (mdPercent: number) => {
        if (!isSplitView(mdSection, htmlSection)) {
          editorContainer.classList.remove('md-split-resize-active', 'is-md-collapsed', 'is-html-collapsed')
          editorContainer.style.removeProperty('--md-split-ratio')
          return
        }

        editorContainer.classList.add('md-split-resize-active')
        editorContainer.classList.remove('is-md-collapsed', 'is-html-collapsed')

        if (mdPercent < MIN_EXPANDED_PERCENT) {
          editorContainer.classList.add('is-md-collapsed')
          collapsedSide = 'md'
          return
        }

        if (mdPercent > 100 - MIN_EXPANDED_PERCENT) {
          editorContainer.classList.add('is-html-collapsed')
          collapsedSide = 'html'
          return
        }

        collapsedSide = null
        lastRatio = mdPercent
        editorContainer.style.setProperty('--md-split-ratio', `${mdPercent}%`)
      }

      const syncVisibility = () => {
        if (isSplitView(mdSection, htmlSection)) {
          applyRatio(collapsedSide === null ? lastRatio : collapsedSide === 'md' ? 0 : 100)
        } else {
          editorContainer.classList.remove('md-split-resize-active', 'is-md-collapsed', 'is-html-collapsed')
          editorContainer.style.removeProperty('--md-split-ratio')
        }
      }

      const startDrag = () => {
        isDragging = true
        document.body.style.cursor = 'col-resize'
        document.body.style.userSelect = 'none'
      }

      const stopDrag = () => {
        if (!isDragging) return
        isDragging = false
        document.body.style.cursor = ''
        document.body.style.userSelect = ''
        editorContainer.style.cursor = ''
      }

      const onPointerDown = (event: PointerEvent) => {
        if (event.button !== 0) return
        if (!isSplitView(mdSection, htmlSection)) return
        if (!isNearSplit(event.clientX, mdSection)) return

        event.preventDefault()
        startDrag()
      }

      const onPointerMove = (event: PointerEvent) => {
        if (isDragging) {
          const rect = editorContainer.getBoundingClientRect()
          const percent = ((event.clientX - rect.left) / rect.width) * 100
          applyRatio(percent)
          return
        }

        if (!isSplitView(mdSection, htmlSection)) {
          editorContainer.style.cursor = ''
          return
        }

        editorContainer.style.cursor = isNearSplit(event.clientX, mdSection) ? 'col-resize' : ''
      }

      const onPointerUp = () => {
        stopDrag()
      }

      const onDoubleClick = (event: MouseEvent) => {
        if (!isSplitView(mdSection, htmlSection)) return
        if (!isNearSplit(event.clientX, mdSection)) return

        collapsedSide = null
        applyRatio(DEFAULT_RATIO)
      }

      editorContainer.addEventListener('pointerdown', onPointerDown)
      editorContainer.addEventListener('pointermove', onPointerMove)
      editorContainer.addEventListener('dblclick', onDoubleClick)
      document.addEventListener('pointermove', onPointerMove)
      document.addEventListener('pointerup', onPointerUp)
      document.addEventListener('pointercancel', onPointerUp)

      const classObserver = new MutationObserver(syncVisibility)
      classObserver.observe(mdSection, { attributes: true, attributeFilter: ['class'] })
      classObserver.observe(htmlSection, { attributes: true, attributeFilter: ['class'] })

      syncVisibility()

      teardown = () => {
        stopDrag()
        editorContainer.classList.remove('md-split-resize-active', 'is-md-collapsed', 'is-html-collapsed')
        editorContainer.style.removeProperty('--md-split-ratio')
        classObserver.disconnect()
        editorContainer.removeEventListener('pointerdown', onPointerDown)
        editorContainer.removeEventListener('pointermove', onPointerMove)
        editorContainer.removeEventListener('dblclick', onDoubleClick)
        document.removeEventListener('pointermove', onPointerMove)
        document.removeEventListener('pointerup', onPointerUp)
        document.removeEventListener('pointercancel', onPointerUp)
        if (attachedContainer === editorContainer) {
          attachedContainer = null
        }
      }
    }

    const mountObserver = new MutationObserver(() => {
      attach()
    })

    mountObserver.observe(container, { childList: true, subtree: true })
    attach()

    return () => {
      disposed = true
      mountObserver.disconnect()
      teardown?.()
    }
  }, [containerRef])
}
